import {createDynamicImportWithRetry} from '@fatso83/retry-dynamic-import';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router';
import BrandMark from '../BrandMark';
import {DEFAULT_LANGUAGE, LANGUAGES, localePath, stripLangPrefix} from '../../languages';

const importMenu = createDynamicImportWithRetry(2, {
    importFunction: url => import(/* @vite-ignore */ new URL(url, import.meta.url).href),
});

const Header = () => {
    const {t, i18n} = useTranslation();
    const headerRef = useRef(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [HeaderMenus, setHeaderMenus] = useState(null);
    const [menusLoading, setMenusLoading] = useState(false);
    const openMenu = async menu => {
        setActiveMenu(menu);
        if (!menu || HeaderMenus || menusLoading) return;
        setMenusLoading(true);
        try {
            const {default: Menus} = await importMenu(() => import('./HeaderMenus'));
            setHeaderMenus(() => Menus);
        } catch (error) {
            setActiveMenu(null);
            console.error(error);
        } finally {
            setMenusLoading(false);
        }
    };
    const openWithKeyboard = (event, menu) => {
        if (event.key === 'Escape') {
            setActiveMenu(null);
            return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        openMenu(menu);
    };
    useEffect(() => {
        if (!activeMenu) return;
        const closeOutside = event => {
            if (!headerRef.current?.querySelector('.header-actions')?.contains(event.target)) setActiveMenu(null);
        };
        document.addEventListener('pointerdown', closeOutside);
        return () => document.removeEventListener('pointerdown', closeOutside);
    }, [activeMenu]);
    useEffect(() => {
        let previousHeight;
        const observer = new ResizeObserver(([entry]) => {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
            if (height === previousHeight) return;
            previousHeight = height;
            document.documentElement.style.setProperty('--header-height', `${height}px`);
        });
        observer.observe(headerRef.current);
        return () => observer.disconnect();
    }, []);
    const {pathname, search, hash} = useLocation();
    useEffect(() => setActiveMenu(null), [pathname, search, hash]);
    const lang = i18n.language;
    const logicalPath = stripLangPrefix(pathname, lang);
    const currentLanguage = LANGUAGES.find(l => l.code === lang) || LANGUAGES.find(l => l.code === DEFAULT_LANGUAGE);
    const navigation = [
        ['/#howItWorks', t('header.howItWorks')],
        ['/#examples', t('header.examples')],
        ['/#faq', t('design.faqNav')],
    ];
    const languageLabel = <><span className="language-full">{currentLanguage.label}</span><span className="language-short">{lang.toUpperCase()}</span></>;
    const menuIcon = (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            {activeMenu === 'navigation' ? <path d="m6 6 12 12M6 18 18 6"/> : <path d="M4 6h16M4 12h16M4 18h16"/>}
        </svg>
    );
    const createLink = <Link id="headerCreateButton" className="design-button" to={localePath('/#generator', lang)}>{t('buttons.create')}</Link>;
    const placeholders = <>
        <div className="dropdown">
            <button type="button" id="languageSwitcher" className="language-switcher dropdown-toggle btn btn-link" title={currentLanguage.label} aria-expanded="false" aria-busy={menusLoading && activeMenu === 'language'} onClick={() => openMenu(activeMenu === 'language' ? null : 'language')} onKeyDown={event => openWithKeyboard(event, 'language')}>{languageLabel}</button>
        </div>
        {createLink}
        <div className="header-mobile-menu dropdown">
            <button type="button" id="headerMenuToggle" className="header-menu-toggle dropdown-toggle btn btn-link" aria-label={t('design.menu')} aria-expanded="false" aria-busy={menusLoading && activeMenu === 'navigation'} onClick={() => openMenu(activeMenu === 'navigation' ? null : 'navigation')} onKeyDown={event => openWithKeyboard(event, 'navigation')}>{menuIcon}</button>
        </div>
    </>;
    return (
        <header className="site-header" ref={headerRef}>
            <div className="design-container header-inner">
                <Link to={localePath('/', lang)} className="brand" aria-label="YourLovely.Place">
                    <BrandMark/>
                    <span>YourLovely<span className="brand-accent">.Place</span></span>
                </Link>
                <nav className="header-nav" aria-label={t('header.home')}>
                    {navigation.map(([path, label]) => <Link key={path} to={localePath(path, lang)}>{label}</Link>)}
                </nav>
                <div className="header-actions">
                    {HeaderMenus ? <HeaderMenus lang={lang} currentLanguage={currentLanguage} logicalPath={logicalPath} search={search} hash={hash}
                            navigation={navigation} activeMenu={activeMenu} onMenuChange={setActiveMenu}
                            languageLabel={languageLabel} menuIcon={menuIcon} createLink={createLink}
                            menuLabel={t('design.menu')} createLabel={t('buttons.create')}/> : placeholders}
                </div>
            </div>
        </header>
    );
};
export default Header;
